import { format, isSameDay, isToday } from "date-fns";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useHabitTrackerVM } from "../hooks/useHabitTrackerVM";
import type { HabitWithStatus } from "../types/habit";
import { buildCategoryInfo } from "../utils/categoryUtils";
import { AffirmationCard } from "./AffirmationCard";
import { CleanupDuplicates } from "./CleanupDuplicates";
import { GratefulCard } from "./GratefulCard";
import { HabitSettings } from "./HabitSettings";
import { InitializeHabits } from "./InitializeHabits";
import { TagChildPicker } from "./TagChildPicker";
import "./HabitTracker.css";

interface HabitTrackerProps {
	userId: string;
	userMenu?: (props: {
		onManageHabits: () => void;
		onLoadDefaults: () => void;
		showLoadDefaults: boolean;
	}) => React.ReactNode;
}

export const HabitTracker: React.FC<HabitTrackerProps> = ({
	userId,
	userMenu,
}) => {
	// View-only state (dialogs)
	const [showInitializer, setShowInitializer] = useState(false);
	const [showCleanup, setShowCleanup] = useState(false);
	const [showSettings, setShowSettings] = useState(false);

	// Tag child picker state
	const [tagChildPickerState, setTagChildPickerState] = useState<{
		tag: HabitWithStatus;
		date: Date;
		position: { x: number; y: number };
	} | null>(null);

	// Long-press detection refs
	const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const longPressTriggered = useRef(false);
	const habitNameLongPressTimer = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	// Cleanup long-press timers on unmount
	useEffect(() => {
		return () => {
			if (longPressTimer.current) {
				clearTimeout(longPressTimer.current);
			}
			if (habitNameLongPressTimer.current) {
				clearTimeout(habitNameLongPressTimer.current);
			}
		};
	}, []);

	// All business logic from the ViewModel
	const vm = useHabitTrackerVM({ userId });

	// Long press handlers for tags with children
	const handleCellPressStart = useCallback(
		(
			habit: HabitWithStatus,
			date: Date,
			event: React.MouseEvent | React.TouchEvent,
		) => {
			const isTag =
				habit.habitType === "tag" &&
				habit.childIds &&
				habit.childIds.length > 0;

			// Only enable long-press for tags with children
			if (!isTag) return;

			longPressTriggered.current = false;
			const clientX =
				"touches" in event ? event.touches[0].clientX : event.clientX;
			const clientY =
				"touches" in event ? event.touches[0].clientY : event.clientY;

			// Prevent context menu on long-press
			event.preventDefault();

			longPressTimer.current = setTimeout(() => {
				longPressTriggered.current = true;
				setTagChildPickerState({
					tag: habit,
					date,
					position: { x: clientX, y: clientY },
				});
			}, 500); // 500ms for long press
		},
		[],
	);

	const handleCellPressEnd = useCallback(() => {
		if (longPressTimer.current) {
			clearTimeout(longPressTimer.current);
			longPressTimer.current = null;
		}
	}, []);

	// Long press handlers for habit name row selection
	const handleHabitNamePressStart = useCallback(
		(habitId: string, event: React.MouseEvent | React.TouchEvent) => {
			event.preventDefault();
			habitNameLongPressTimer.current = setTimeout(() => {
				vm.selectHabit(habitId);
			}, 500);
		},
		[vm],
	);

	const handleHabitNamePressEnd = useCallback(() => {
		if (habitNameLongPressTimer.current) {
			clearTimeout(habitNameLongPressTimer.current);
			habitNameLongPressTimer.current = null;
		}
	}, []);

	const handleCellClick = useCallback(
		(habit: HabitWithStatus, date: Date) => {
			// If long press was triggered, don't also do click action
			if (longPressTriggered.current) {
				longPressTriggered.current = false;
				return;
			}
			vm.toggleEntry(habit.id, date);
		},
		[vm],
	);

	// Get live tag data (refreshes when entries change)
	const liveTag = useMemo((): HabitWithStatus | null => {
		if (!tagChildPickerState) return null;
		return vm.habits.find((h) => h.id === tagChildPickerState.tag.id) || null;
	}, [tagChildPickerState, vm.habits]);

	// Get child habits with status for the tag child picker
	const tagChildHabits = useMemo((): HabitWithStatus[] => {
		if (!liveTag) return [];
		const childIds = liveTag.childIds || [];
		return vm.habits.filter((h) => childIds.includes(h.id));
	}, [liveTag, vm.habits]);

	// Handler for tag child selection (toggles entry, picker stays open)
	const handleTagChildSelect = useCallback(
		async (childId: string | null) => {
			if (!tagChildPickerState) return;
			if (childId === null) {
				// Generic entry on the tag itself
				vm.toggleEntry(tagChildPickerState.tag.id, tagChildPickerState.date);
			} else {
				// Entry on the selected child habit
				vm.toggleEntry(childId, tagChildPickerState.date);
			}
			// Don't close picker - let user toggle multiple items
		},
		[tagChildPickerState, vm],
	);

	// Helper: check if a habit is row-selected (directly or as child of selected tag)
	const isHabitRowSelected = useCallback(
		(habitId: string): boolean => {
			const sel = vm.selection;
			if (sel?.type !== "row") return false;
			if (sel.habitId === habitId) return true;
			// Check if this habit is a child of the selected tag
			const selectedHabit = vm.habits.find((h) => h.id === sel.habitId);
			return selectedHabit?.childIds?.includes(habitId) ?? false;
		},
		[vm.selection, vm.habits],
	);

	// Loading screen
	if (vm.isLoading) {
		return (
			<div
				className="container"
				style={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
					gap: "20px",
				}}
			>
				<div
					style={{
						fontSize: "48px",
						animation: "pulse 1.5s ease-in-out infinite",
					}}
				>
					⏳
				</div>
				<div style={{ fontSize: "18px", color: "#94a3b8" }}>
					Loading your habits...
				</div>
				<div style={{ fontSize: "14px", color: "#64748b" }}>
					Setting up your tracking dashboard
				</div>
			</div>
		);
	}

	const toggleExpandCollapse = () => {
		if (vm.allExpanded) {
			vm.collapseAll();
		} else {
			vm.expandAll();
		}
	};

	// Helper to generate column/cell class names for date columns
	const getDateColumnClass = (
		baseClass: string,
		isTodayDate: boolean,
		isSelected: boolean,
	): string => {
		const classes = [baseClass];
		if (isTodayDate) classes.push(`${baseClass.split("-")[0]}-today`);
		if (isSelected && !isTodayDate)
			classes.push(`${baseClass.split("-")[0]}-selected`);
		return classes.join(" ");
	};

	// Helper to handle date header click
	const handleDateHeaderClick = (
		date: Date,
		isTodayDate: boolean,
		isSelected: boolean,
	) => {
		if (isTodayDate) return; // Today is not selectable
		vm.selectDate(isSelected ? null : date);
	};

	return (
		<div className="container">
			{vm.entryError && (
				<div className="error-banner" role="alert">
					{vm.entryError}
					<button
						type="button"
						onClick={vm.clearEntryError}
						aria-label="Dismiss error"
					>
						×
					</button>
				</div>
			)}
			<div className="week-header">
				<div className="week-title">
					{vm.zoomedSection ? (
						<>
							{buildCategoryInfo(vm.zoomedSection).name}
							<button className="zoom-back-btn" onClick={vm.zoomOut}>
								← Back
							</button>
						</>
					) : (
						<span
							className={`current-day ${vm.selection ? "selected-day" : ""}`}
							onClick={() => vm.selectDate(null)}
							style={{ cursor: vm.selection ? "pointer" : "default" }}
							title={vm.selection ? "Click to return to today" : undefined}
						>
							{(() => {
								const sel = vm.selection;
								if (sel?.type === "column")
									return format(sel.date, "EEEE, MMM d");
								if (sel?.type === "row")
									return `Editing: ${vm.habits.find((h) => h.id === sel.habitId)?.name ?? "Unknown"}`;
								return format(new Date(), "EEEE, MMM d");
							})()}
						</span>
					)}
				</div>
				<div className="view-toggle">
					{!vm.zoomedSection && (
						<button className="toggle-btn" onClick={toggleExpandCollapse}>
							{vm.allExpanded ? "Collapse All" : "Expand All"}
						</button>
					)}
					{userMenu?.({
						onManageHabits: () => setShowSettings(true),
						onLoadDefaults: () => {
							if (!vm.hasNoHabits) {
								if (
									!window.confirm(
										"This will DELETE all your current habits and entries, then load demo data. This cannot be undone. Continue?",
									)
								) {
									return;
								}
							}
							setShowInitializer(true);
						},
						showLoadDefaults: true,
					})}
				</div>
			</div>

			<table>
				<thead>
					<tr>
						<th className="col-habit">Habit</th>
						<th className="col-status">●</th>
						{vm.weekDates.map((date) => {
							const isTodayDate = isToday(date);
							const isSelected = Boolean(
								vm.selection?.type === "column" &&
									isSameDay(date, vm.selection.date),
							);
							return (
								<th
									key={date.toISOString()}
									className={getDateColumnClass(
										"col-day",
										isTodayDate,
										isSelected,
									)}
									onClick={() =>
										handleDateHeaderClick(date, isTodayDate, isSelected)
									}
									style={{ cursor: isTodayDate ? "default" : "pointer" }}
									title={isTodayDate ? undefined : "Click to select this day"}
								>
									{format(date, "EEE").slice(0, 2)}
									<br />
									{format(date, "d")}
								</th>
							);
						})}
						<th className="col-total">Total</th>
					</tr>
				</thead>
				<tbody>
					{vm.sections
						.filter(
							(section) =>
								!vm.zoomedSection || section.category === vm.zoomedSection,
						)
						.map((section) => {
							const summary = vm.getCategorySummary(section.habits);
							return (
								<React.Fragment key={section.category}>
									<tr className="section-header">
										<td colSpan={10}>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<div
													className="section-title"
													onClick={() => vm.toggleSection(section.category)}
												>
													<span
														className={`section-arrow ${section.isCollapsed ? "collapsed" : ""}`}
													>
														▼
													</span>
													<span
														className="section-indicator"
														style={{ background: section.color }}
													/>
													{section.name}
													{vm.zoomedSection ? (
														<button
															className="zoom-btn"
															onClick={(e) => {
																e.stopPropagation();
																vm.zoomOut();
															}}
															title="Back to all categories"
														>
															← Back
														</button>
													) : (
														<button
															className="zoom-btn"
															onClick={(e) => {
																e.stopPropagation();
																vm.zoomIn(section.category);
															}}
															title="Focus on this category"
														>
															🔍
														</button>
													)}
												</div>
												<div className="section-summary">
													<span className="summary-stat">
														<span
															className={`stat-value stat-${summary.todayStatus}`}
														>
															{summary.doneToday}/{summary.dueToday}
														</span>
													</span>
													<span className="summary-stat">
														<span
															className={`stat-value stat-${summary.totalStatus}`}
														>
															{summary.met}/{summary.total}
														</span>
													</span>
												</div>
											</div>
										</td>
									</tr>
									{!section.isCollapsed &&
										vm.habitTree
											.filter(
												(node) => node.habit.category === section.category,
											)
											.map((treeNode) => {
												const habit = treeNode.habit;
												const depth = treeNode.depth;
												const isTag = treeNode.isTag;
												const isExpanded = treeNode.isExpanded;
												const hasChildren = treeNode.hasChildren;
												const rowSelected = isHabitRowSelected(habit.id);

												return (
													<tr key={habit.id} className="section-row">
														<td
															className={`col-habit ${rowSelected ? "row-selected" : ""}`}
															onMouseDown={(e) =>
																handleHabitNamePressStart(habit.id, e)
															}
															onMouseUp={handleHabitNamePressEnd}
															onMouseLeave={handleHabitNamePressEnd}
															onTouchStart={(e) =>
																handleHabitNamePressStart(habit.id, e)
															}
															onTouchEnd={handleHabitNamePressEnd}
														>
															<div
																className="habit-name"
																style={{ paddingLeft: `${depth * 16}px` }}
															>
																{isTag && hasChildren && (
																	<span
																		className={`tag-arrow ${isExpanded ? "" : "collapsed"}`}
																		onClick={(e) => {
																			e.stopPropagation();
																			vm.toggleTagExpanded(habit.id);
																		}}
																		style={{
																			cursor: "pointer",
																			marginRight: 4,
																		}}
																	>
																		▼
																	</span>
																)}
																{habit.name}
																<span className="habit-target">
																	({habit.targetPerWeek}/w)
																</span>
															</div>
														</td>
														<td>
															<span className={`status status-${habit.status}`}>
																{vm.getStatusIcon(habit.status)}
															</span>
														</td>
														{vm.weekDates.map((date) => {
															const cellDisplay = vm.getCellDisplay(
																habit,
																date,
															);
															const isTodayDate = isToday(date);
															const isColumnSelected = Boolean(
																vm.selection?.type === "column" &&
																	isSameDay(date, vm.selection.date),
															);
															const cellClass = [
																cellDisplay.className,
																getDateColumnClass(
																	"cell",
																	isTodayDate,
																	isColumnSelected,
																),
																rowSelected ? "cell-selected" : "",
																isTag ? "tag-cell" : "",
															]
																.filter(Boolean)
																.join(" ");
															return (
																<td
																	key={date.toISOString()}
																	className={cellClass}
																	onClick={() => handleCellClick(habit, date)}
																	onMouseDown={(e) =>
																		handleCellPressStart(habit, date, e)
																	}
																	onMouseUp={handleCellPressEnd}
																	onMouseLeave={handleCellPressEnd}
																	onTouchStart={(e) =>
																		handleCellPressStart(habit, date, e)
																	}
																	onTouchEnd={handleCellPressEnd}
																	style={{ cursor: "pointer" }}
																>
																	{cellDisplay.content}
																</td>
															);
														})}
														<td
															className={`total total-${
																habit.currentWeekCount >= habit.targetPerWeek
																	? "good"
																	: habit.currentWeekCount > 0
																		? "warn"
																		: "bad"
															}`}
														>
															{habit.currentWeekCount}/{habit.targetPerWeek}
														</td>
													</tr>
												);
											})}
								</React.Fragment>
							);
						})}
				</tbody>
			</table>

			<div className="summary-bar summary-bar-compact">
				<div className="summary-item">
					<span className="summary-label">Due:</span>
					<span className="summary-value value-today">
						{vm.summaryStats.dueToday}
					</span>
				</div>
				<div className="summary-item">
					<span className="summary-label">Done:</span>
					<span className="summary-value value-done">
						{vm.summaryStats.doneToday}
					</span>
				</div>
				<div className="summary-item">
					<span className="summary-label">Late:</span>
					<span className="summary-value value-overdue">
						{vm.summaryStats.overdue}
					</span>
				</div>
			</div>

			<AffirmationCard userId={userId} />
			<GratefulCard userId={userId} />

			{showInitializer && (
				<InitializeHabits
					userId={userId}
					onComplete={() => setShowInitializer(false)}
				/>
			)}

			{showCleanup && (
				<CleanupDuplicates
					userId={userId}
					onComplete={() => {
						setShowCleanup(false);
						window.location.reload();
					}}
				/>
			)}

			{showSettings && (
				<HabitSettings
					userId={userId}
					onClose={() => setShowSettings(false)}
					onUpdate={() => {
						setShowSettings(false);
						window.location.reload();
					}}
				/>
			)}

			{tagChildPickerState && liveTag && (
				<TagChildPicker
					tag={liveTag}
					childHabits={tagChildHabits}
					date={tagChildPickerState.date}
					position={tagChildPickerState.position}
					onSelectChild={handleTagChildSelect}
					onClose={() => setTagChildPickerState(null)}
				/>
			)}
		</div>
	);
};
